import { Switch, Route } from "wouter";
import { lazy, Suspense } from "react";
// SidebarProvider removed — not used on Carlorbiz site, was constraining page width
import { CMSProvider } from "@/contexts/CMSContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ChatProvider } from "@/contexts/ChatContext";
import type { TabContent, TabFolder, AppSettings, DecisionTree } from "@/types/cms";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AboutMe from "@/pages/AboutMe";
import Services from "@/pages/Services";
import Insights from "@/pages/Insights";
import Contact from "@/pages/Contact";
import CampaignLanding from "@/pages/CampaignLanding";
import { CAMPAIGNS } from "@/data/campaigns";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import { NeraChatbot } from "@/components/chat";
import { BackToTop } from "@/components/BackToTop";
import { ScrollToTop } from "@/components/ScrollToTop";
// TabDetail is intentionally NOT lazy — renderToString does not finish
// Suspense boundaries on the server, so a lazy page would emit only the
// fallback during SSR. The cost (~70 kB more in the main bundle) is the
// price of having every detail URL pre-rendered for crawlers.
import TabDetail from "@/pages/TabDetail";

// Lazy load heavy pages to reduce initial bundle size. These routes are NOT
// in the SSR enumeration (admin/auth/demo) so the Suspense fallback is fine
// for crawlers — they're not meant to be indexed anyway.
const Home = lazy(() => import("@/pages/Home"));
const Admin = lazy(() => import("@/pages/Admin"));
const FeedbackPage = lazy(() => import("@/pages/FeedbackPage"));
const FollowUpPage = lazy(() => import("@/pages/FollowUpPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPassword"));
const NeraDemoPage = lazy(() => import("@/pages/NeraDemoPage"));

/** Only render Nera for authenticated users (TPAs / employees) */
function AuthenticatedNera() {
  const { user } = useAuth();
  if (!user) return null;
  return <NeraChatbot />;
}

export interface InitialData {
  tabs?: TabContent[];
  settings?: AppSettings;
  folders?: TabFolder[];
  decisionTrees?: DecisionTree[];
}

function App({ initialData }: { initialData?: InitialData } = {}) {
  return (
    <AuthProvider>
      <ChatProvider>
        <CMSProvider
          initialTabs={initialData?.tabs}
          initialSettings={initialData?.settings}
          initialFolders={initialData?.folders}
          initialDecisionTrees={initialData?.decisionTrees}
        >
            <ScrollToTop />
            <Switch>
              <Route path="/" component={Landing} />
              <Route path="/services" component={Services} />
              <Route path="/about-me" component={AboutMe} />
              <Route path="/contact" component={Contact} />
              <Route path="/insights" component={Insights} />
              <Route path="/lp/:slug">
                <CampaignLanding campaigns={CAMPAIGNS} />
              </Route>
              <Route path="/hub">
                <Suspense fallback={<div className="flex h-screen items-center justify-center">Loading...</div>}>
                  <Home />
                </Suspense>
              </Route>
              <Route path="/login" component={Login} />
              <Route path="/nera-demo">
                <Suspense fallback={<div className="flex h-screen items-center justify-center">Loading...</div>}>
                  <NeraDemoPage />
                </Suspense>
              </Route>
              <Route path="/reset-password">
                <Suspense fallback={<div className="flex h-screen items-center justify-center">Loading...</div>}>
                  <ResetPasswordPage />
                </Suspense>
              </Route>
              <Route path="/admin">
                <Suspense fallback={<div className="flex h-screen items-center justify-center">Loading...</div>}>
                  <ProtectedRoute component={Admin} />
                </Suspense>
              </Route>
              <Route path="/feedback/:campaignSlug">
                <Suspense fallback={<div className="flex h-screen items-center justify-center">Loading...</div>}>
                  <FeedbackPage />
                </Suspense>
              </Route>
              <Route path="/follow-up">
                <Suspense fallback={<div className="flex h-screen items-center justify-center">Loading...</div>}>
                  <FollowUpPage />
                </Suspense>
              </Route>
              <Route path="/:folderSlug/:tabSlug" component={TabDetail} />
              <Route>404 Page Not Found</Route>
            </Switch>
            <Toaster />
            <BackToTop />
            <AuthenticatedNera />
        </CMSProvider>
      </ChatProvider>
    </AuthProvider>
  );
}

export default App;
